/**
 * Bandwidth-alerting API client (migration 108 backend, Jason 2026-08-03).
 *
 *   GET  /api/bandwidth/alerts?open=1              list
 *   GET  /api/bandwidth/alerts/count               badge count
 *   POST /api/bandwidth/alerts/{id}/ack            acknowledge one
 *   GET  /api/bandwidth/history?scu=SBnnn&days=N   chart data
 *   GET  /api/bandwidth/thresholds                 threshold table
 *   PUT  /api/bandwidth/thresholds                 upsert one row
 */
import { apiFetch } from "./apiClient";

export type BandwidthAlertKind = "daily_over_threshold" | "spike_vs_prior_7d";

export interface BandwidthAlert {
  alert_id: number;
  device_id: number;
  device_sn: string | null;
  smartbox_id: number | null;
  scu_name: string | null;
  customer_company_id: number | null;
  customer_name: string | null;
  alert_kind: BandwidthAlertKind;
  report_date: string | null;
  threshold_value: number;
  actual_value: number;
  detail: Record<string, unknown>;
  fired_utc: string | null;
  ack_utc: string | null;
  ack_by_user_id: number | null;
  resolved_utc: string | null;
}

export interface BandwidthHistoryPoint {
  date: string;
  up_mb: number;
  down_mb: number;
  total_mb: number;
}

export interface BandwidthHistory {
  scu_name: string;
  device_id: number;
  device_sn: string | null;
  since: string;
  until: string;
  days: number;
  data: BandwidthHistoryPoint[];
}

export interface BandwidthTopRow {
  device_id: number;
  device_sn: string | null;
  up_mb: number;
  down_mb: number;
  total_mb: number;
  scu_name: string | null;
  smartbox_name: string | null;
  work_center: string | null;
  customer_name: string | null;
  exceeds_gb: boolean;
}

export interface BandwidthTopResponse {
  report_date: string;
  limit: number;
  gb_threshold: number;
  data: BandwidthTopRow[];
}

/** One box in the replacement-priority view — ranked by PEPLINK (metered)
 *  usage, with the box's Starlink (flat-rate) usage + account status alongside.
 *  High peplink + a lost/low/not-in_service Starlink = replace the dish. */
export interface BandwidthPriorityRow {
  scu_name: string | null;
  smartbox_name: string | null;
  customer_name: string | null;
  work_center: string | null;
  peplink_up_mb: number;
  peplink_down_mb: number;
  peplink_total_mb: number;
  est_cost_usd: number;
  starlink_up_mb: number;
  starlink_down_mb: number;
  starlink_total_mb: number;
  starlink_mtd_mb: number;
  starlink_asset_status: string | null;
  service_line: string | null;
  dish_serial: string | null;
}

export interface BandwidthPriorityResponse {
  report_date: string;
  mtd_start: string;
  limit: number;
  cents_per_mb: number;
  starlink_block_gb: number;
  billing_cycle_day: number;
  gb_threshold: number;
  data: BandwidthPriorityRow[];
}

export interface StarlinkHistoryPoint {
  date: string;
  up_mb: number;
  down_mb: number;
  total_mb: number;
}

export interface StarlinkHistory {
  scu_name: string;
  days: number;
  data: StarlinkHistoryPoint[];
}

export interface BandwidthThresholdRow {
  threshold_id: number;
  scope_kind: "global" | "customer";
  scope_id: number | null;
  scope_name: string;
  daily_gb_alert: number;
  spike_ratio_alert: number;
  updated_utc: string | null;
}

/** The editable threshold shape the Settings tab consumes. The server also
 *  returns display extras (scope_name / customer_name / threshold_id) which
 *  ride along under the index signature. */
export interface BandwidthThreshold {
  scope_kind: "global" | "customer";
  scope_id: number | null;
  daily_gb_alert: number;
  spike_ratio_alert: number;
  updated_utc: string | null;
  [k: string]: unknown;
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, token, init);
}

export function fetchBandwidthAlerts(
  token: string,
  q: { onlyOpen?: boolean; limit?: number } = {},
): Promise<{ data: BandwidthAlert[]; count: number }> {
  const p = new URLSearchParams();
  p.set("open", q.onlyOpen === false ? "0" : "1");
  if (q.limit != null) p.set("limit", String(q.limit));
  return req(token, `/api/bandwidth/alerts?${p}`);
}

export function fetchBandwidthAlertCount(
  token: string,
): Promise<{ open_count: number }> {
  return req(token, "/api/bandwidth/alerts/count");
}

export function ackBandwidthAlert(
  token: string, alertId: number,
): Promise<{ ok: boolean; alert_id: number }> {
  return req(token, `/api/bandwidth/alerts/${alertId}/ack`, { method: "POST" });
}

export interface BandwidthMonthlyRow {
  month: string;          // "YYYY-MM"
  up_mb: number;
  down_mb: number;
  total_mb: number;
  device_count: number;
  is_partial: boolean;    // true = current month-to-date
  fetched_utc: string | null;
}

export interface BandwidthMonthlyResponse {
  months: number;
  data: BandwidthMonthlyRow[];
}

/** Fleet-wide monthly data usage (all devices, all groups, all WANs) from
 *  InControl. Platform-only server-side. */
export function fetchBandwidthMonthly(
  token: string, months = 4,
): Promise<BandwidthMonthlyResponse> {
  return req(token, `/api/bandwidth/monthly?months=${encodeURIComponent(months)}`);
}

export function fetchBandwidthTop(
  token: string, q: { date?: string; limit?: number } = {},
): Promise<BandwidthTopResponse> {
  const p = new URLSearchParams();
  if (q.date) p.set("date", q.date);
  if (q.limit != null) p.set("limit", String(q.limit));
  const qs = p.toString();
  return req(token, `/api/bandwidth/top${qs ? `?${qs}` : ""}`);
}

export function fetchBandwidthHistory(
  token: string, scu: string, days = 30,
): Promise<BandwidthHistory> {
  const p = new URLSearchParams({ scu, days: String(days) });
  return req(token, `/api/bandwidth/history?${p}`);
}

export function fetchBandwidthPriority(
  token: string, q: { date?: string; limit?: number } = {},
): Promise<BandwidthPriorityResponse> {
  const p = new URLSearchParams();
  if (q.date) p.set("date", q.date);
  if (q.limit != null) p.set("limit", String(q.limit));
  const qs = p.toString();
  return req(token, `/api/bandwidth/priority${qs ? `?${qs}` : ""}`);
}

export function fetchStarlinkBandwidthHistory(
  token: string, scu: string, days = 30,
): Promise<StarlinkHistory> {
  const p = new URLSearchParams({ scu, days: String(days) });
  return req(token, `/api/bandwidth/starlink/history?${p}`);
}

export function fetchBandwidthThresholds(
  token: string,
): Promise<{ data: BandwidthThreshold[] }> {
  return req(token, "/api/bandwidth/thresholds");
}

export function upsertBandwidthThreshold(
  token: string,
  body: {
    scope_kind: "global" | "customer";
    scope_id?: number | null;
    daily_gb_alert: number;
    spike_ratio_alert: number;
  },
): Promise<unknown> {
  return req(token, "/api/bandwidth/thresholds", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Human-readable GB helper. Accepts MB, returns "3.4 GB" / "745 MB". */
export function formatMB(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}
